import React from 'react';
import CurrencySelector from './CurrencySelector';
import RangeSlider from './RangeSlider';
import {
  MAX_INVESTMENT_AMOUNT_USD,
  MIN_INVESTMENT_AMOUNT,
  MIN_INVESTMENT_AMOUNT_SEK,
} from '../../../constants/profileFieldLimits';
import {
  investmentMaxUnset,
  keepRangeOnCurrencyChange,
  rangeAfterMinChange,
} from '../../../utils/investmentRangeEdit';
import { loadExchangeRates } from '../../../utils/loadExchangeRates';
import { maxInvestmentInCurrency } from '../../../utils/minStartupAsk';

interface RangeSelectorProps {
  currency: string;
  range: [number, number]; // [min, max] — always true amounts
  onCurrencyChange: (cur: string) => void;
  onRangeChange: (range: [number, number]) => void;
  multiplier?: number;
  min?: number;
  max?: number;
  step?: number;
  isMultiplierActive?: boolean;
  onMultiplierToggle?: (active: boolean) => void;
  hideRangeText?: boolean;
  scaleExponent?: number;
  isEmpty?: boolean;
}

function snapToStep(value: number, step: number): number {
  if (!Number.isFinite(value) || step <= 0) return value;
  return Math.round(value / step) * step;
}

/**
 * Investor investment range. Values are always true amounts.
 * Default ceiling is max/10 (~20M). +10x multiplies only the max by 10 and unlocks ~200M.
 * The min never jumps. Turning +10x off never divides — it only re-locks the ~20M ceiling.
 */
const RangeSelector: React.FC<RangeSelectorProps> = ({
  currency,
  range,
  onCurrencyChange,
  onRangeChange,
  multiplier = 1,
  min = 0,
  max = MAX_INVESTMENT_AMOUNT_USD,
  step = 1000,
  isMultiplierActive: externalIsMultiplierActive,
  onMultiplierToggle: externalOnMultiplierToggle,
  hideRangeText = false,
  scaleExponent = 1,
  isEmpty = false,
}) => {
  const [internalIsMultiplierActive, setInternalIsMultiplierActive] = React.useState(false);
  const isMultiplierActive =
    externalIsMultiplierActive !== undefined
      ? externalIsMultiplierActive
      : internalIsMultiplierActive;
  const [exchangeRates, setExchangeRates] = React.useState<Record<string, number> | null>(null);
  const [loadingRates, setLoadingRates] = React.useState(true);
  const [previousCurrency, setPreviousCurrency] = React.useState<string>(currency || 'eur');

  const setMultiplierActive = (active: boolean) => {
    if (externalOnMultiplierToggle) {
      externalOnMultiplierToggle(active);
    } else {
      setInternalIsMultiplierActive(active);
    }
  };

  React.useEffect(() => {
    let cancelled = false;
    loadExchangeRates()
      .then(rates => {
        if (!cancelled) setExchangeRates(rates);
      })
      .finally(() => {
        if (!cancelled) setLoadingRates(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Base max is in USD; convert to the selected currency.
  const dynamicMax = maxInvestmentInCurrency(currency || 'usd', exchangeRates, max);
  const currencyMin = (currency || '').toLowerCase() === 'sek' ? MIN_INVESTMENT_AMOUNT_SEK : min;
  // A new profile is stored as [0, 0]. That maximum is unset, not a real cap.
  const maxUnset = investmentMaxUnset(isEmpty, range);

  const lowMax =
    multiplier > 1 ? Math.max(step, snapToStep(dynamicMax / multiplier, step)) : dynamicMax;
  const needsUnlock =
    multiplier > 1 && !isMultiplierActive && !isEmpty && (range[0] > lowMax || range[1] > lowMax);
  const unlocked = (isMultiplierActive || needsUnlock) && multiplier > 1;
  const activeMax = unlocked ? dynamicMax : lowMax;

  React.useEffect(() => {
    if (!needsUnlock) return;
    setMultiplierActive(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsUnlock]);

  const safeRange: [number, number] = [
    typeof range[0] === 'number' && !isNaN(range[0]) ? Math.min(range[0], activeMax) : min,
    typeof range[1] === 'number' && !isNaN(range[1]) ? Math.min(range[1], activeMax) : activeMax,
  ];

  const handleMultiplierToggle = () => {
    if (multiplier <= 1) return;

    const minAmount = typeof range[0] === 'number' && !isNaN(range[0]) ? range[0] : 0;
    const maxAmount = typeof range[1] === 'number' && !isNaN(range[1]) ? range[1] : 0;

    if (unlocked) {
      // Turn off: keep amounts; only clamp if above the ~20M ceiling. Never ÷10.
      onRangeChange([Math.min(minAmount, lowMax), maxAmount > 0 ? Math.min(maxAmount, lowMax) : 0]);
      setMultiplierActive(false);
      return;
    }

    // Turn on: multiply only the max by 10 (capped). Min stays put.
    if (maxAmount > 0) {
      const nextMax = Math.min(dynamicMax, snapToStep(maxAmount * multiplier, step));
      onRangeChange([Math.min(minAmount, nextMax), nextMax]);
    }
    setMultiplierActive(true);
  };

  const handleCurrencyChange = (newCurrency: string) => {
    if (newCurrency !== previousCurrency) {
      const floor =
        (newCurrency || '').toLowerCase() === 'sek'
          ? MIN_INVESTMENT_AMOUNT_SEK
          : min || MIN_INVESTMENT_AMOUNT;
      const next = keepRangeOnCurrencyChange(range, floor);
      if (next[0] !== range[0] || next[1] !== range[1]) {
        onRangeChange(next);
      }
    }
    setPreviousCurrency(newCurrency);
    onCurrencyChange(newCurrency);
  };

  return (
    <div className="space-y-4">
      <CurrencySelector value={currency || 'eur'} onChange={handleCurrencyChange} />
      {loadingRates && (
        <div className="text-sm text-gray-400 text-left">Loading exchange rates...</div>
      )}
      <RangeSlider
        minValue={safeRange[0]}
        maxValue={maxUnset ? 0 : safeRange[1]}
        onMinChange={val => onRangeChange(rangeAfterMinChange(safeRange, val, maxUnset))}
        onMaxChange={val => onRangeChange([safeRange[0], Math.min(val, activeMax)])}
        currency={currency}
        multiplier={multiplier}
        min={currencyMin}
        max={activeMax}
        step={step}
        isMultiplierActive={unlocked}
        onMultiplierToggle={handleMultiplierToggle}
        multiplyDisplay={false}
        hideRangeText={hideRangeText}
        scaleExponent={scaleExponent}
        isEmpty={isEmpty}
      />
    </div>
  );
};

export default RangeSelector;
